import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UserProfileApi, parseProfileValue } from '@/services/users/userProfileApi'
import { useCurrentUser } from './UserContext'

type Profile = Record<string, string>
/** Keys to store, as JSON-able values; `null` removes a key. */
type Values = Record<string, unknown>

interface UserProfileContextValue {
  /** Every key as stored — JSON strings. Undefined until the profile has loaded. */
  profile: Profile | undefined
  isLoading: boolean
  /** One key parsed from JSON; `fallback` when unset, unreadable or still loading. */
  read: <T>(key: string, fallback: T) => T
  /**
   * The profile as the server has it right now, for read-modify-write: a value
   * changed in another tab or in the old UI is not lost to this tab's copy.
   * Leaves the shared copy alone, so anything shown ahead of a save stays.
   */
  latest: () => Promise<Profile>
  /**
   * Shows `values` at once without saving them — or the values worked out from
   * the profile as shown now. Returns a function that puts those keys back as
   * they were.
   */
  preview: (values: Values | ((current: Profile) => Values)) => () => void
  /**
   * Saves `values` (JSON-able; `null` removes a key) in one request. Shown at
   * once and put back if the save fails, which then throws.
   */
  save: (values: Values) => Promise<void>
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null)

/**
 * The signed-in user's profile, loaded once and shared, so every preference
 * kept in it reads from the same request.
 */
export function UserProfileProvider({ children }: { children: ReactNode }) {
  const user = useCurrentUser()
  const userId = user?.id
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['userProfile', userId], [userId])

  const { data: profile, isLoading } = useQuery({
    queryKey,
    queryFn: UserProfileApi.getAll,
    enabled: !!userId,
    staleTime: Infinity,
  })

  const preview = useCallback(
    (change: Values | ((current: Profile) => Values)) => {
      const before = queryClient.getQueryData<Profile>(queryKey) ?? {}
      const values = typeof change === 'function' ? change(before) : change
      const patch = (profile: Profile = {}, next: (key: string) => string | undefined) => {
        const result = { ...profile }
        for (const key of Object.keys(values)) {
          const value = next(key)
          if (value === undefined) delete result[key]
          else result[key] = value
        }
        return result
      }
      queryClient.setQueryData<Profile>(queryKey, (current) =>
        patch(current, (key) => (values[key] === null ? undefined : JSON.stringify(values[key])))
      )
      // Only the keys previewed go back, so a save of other keys meanwhile stays.
      return () => queryClient.setQueryData<Profile>(queryKey, (current) => patch(current, (key) => before[key]))
    },
    [queryClient, queryKey]
  )

  const save = useCallback(
    async (values: Values) => {
      if (!userId) throw new Error('Not signed in')
      const undo = preview(values)
      try {
        await UserProfileApi.setMany(userId, values)
      } catch (error) {
        undo()
        throw error
      }
    },
    [userId, preview]
  )

  const value = useMemo<UserProfileContextValue>(
    () => ({
      profile,
      isLoading,
      read: (key, fallback) => parseProfileValue(profile?.[key], fallback),
      latest: UserProfileApi.getAll,
      preview,
      save,
    }),
    [profile, isLoading, preview, save]
  )

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
}

export function useUserProfile() {
  const context = useContext(UserProfileContext)
  if (!context) throw new Error('useUserProfile must be used inside UserProfileProvider')
  return context
}
